import { LayerSpecification, Map } from "@iventis/mapbox-gl";
import { SlidingWindowThreshold } from "@iventis/utilities/src/sliding-window-threshold";
import { Polygon } from "geojson";
import { MapStore } from "../../../../types/store-schema";
import { IVENTIS_MAP_BACKGROUND_SLOT_ID, MAPBOX_MAX_ZOOM, MAPBOX_MIN_ZOOM } from "../../../constants";
import { MapBackgroundDataStore, MapBackground as MapBackgroundType } from "../../../../data-store/basemap/map-background-data-store";
import { Basemap } from "../basemap";
import { layerScaleModifier } from "../../export-scale-helpers";
import { MapboxPolygonFilter } from "../../filters/mapbox-polygon-filter";
import { isMapboxTileSourceError } from "../../types/errors-type-helpers";
import { MapboxError } from "../../types/errors-types";

export class MapboxMapBackground extends Basemap<MapBackgroundType, LayerSpecification["type"]> {
    private readonly state: MapStore;

    public readonly id: string;

    private onFinishedLoadingBackground: () => void;

    private readonly mapBackgroundPolygonFilter: MapboxPolygonFilter;

    private onErrorRef: (event: MapboxError) => void;

    private readonly onTileErrorTracker: SlidingWindowThreshold<number>;

    private readonly onTileJSONErrorTracker: SlidingWindowThreshold<number>;

    constructor(
        id: string,
        mapBackgroundDataStore: MapBackgroundDataStore,
        map: Map,
        state: MapStore,
        onFinishedLoadingBackground: () => void,
        onMapBackgroundError: (id: string, status: number, entity: "tile" | "tile-json") => void,
        layerScale?: number,
        polygonFilter?: Polygon
    ) {
        super(id, mapBackgroundDataStore, map);
        this.state = state;
        this.onFinishedLoadingBackground = onFinishedLoadingBackground;

        // Tracks tile errors, need 3 errors within 1 second
        this.onTileErrorTracker = new SlidingWindowThreshold<number>({
            eventThreshold: 3,
            timeWindowMs: 1000,
            callbackCooldownMs: 3000,
            callback: ({ mostCommon }) => {
                onMapBackgroundError(this.id, mostCommon, "tile");
            },
        });

        // Tracks TileJSON fetch errors, need 1 error within 1 second
        // Mapbox attmepts to get the tileset metdata twice, if the first attempt fails
        this.onTileJSONErrorTracker = new SlidingWindowThreshold<number>({
            eventThreshold: 1,
            timeWindowMs: 1000,
            callbackCooldownMs: 3000,
            callback: ({ mostCommon }) => {
                onMapBackgroundError(this.id, mostCommon, "tile-json");
            },
        });

        if (polygonFilter) {
            this.mapBackgroundPolygonFilter = new MapboxPolygonFilter(polygonFilter, this.map, {
                getLayerIds: () => this.layers.map((l) => l.id),
            });
        }

        this.addToMap(layerScale);
    }

    protected async addToMap(layerScale?: number) {
        const { layers, sprite, sources, metaData } = await this.basemapDataStore.getBasemap(this.id);

        // As we are awaiting above, before we continue check the map background hasn't been removed
        if (this.removed) {
            return;
        }

        this.onErrorRef = this.onError.bind(this);
        this.map.on("error", this.onErrorRef);

        Object.entries(sources).forEach(([sourceId, source]) => {
            if (!this.map.getSource(sourceId)) {
                this.map.addSource(sourceId, source);
                this.sourceIds.push(sourceId);
            }
        });

        if (layerScale != null) {
            layerScaleModifier(layers, layerScale);
        }

        for (const layer of layers) {
            // If layer is already in the map don't add it again
            if (this.map.getLayer(layer.id)) {
                continue;
            }

            layer.metadata = { type: "map-background", mapBackgroundName: metaData?.name };

            if (this.mapBackgroundPolygonFilter && layer.type === "background") {
                this.mapBackgroundPolygonFilter.replaceBackgroundLayer(layer, IVENTIS_MAP_BACKGROUND_SLOT_ID);
                continue;
            }

            this.layers.push({ id: layer.id, type: layer.type });
            this.map.addLayer(layer, IVENTIS_MAP_BACKGROUND_SLOT_ID);
        }

        if (!this.state.change.value.buildings3D.value) {
            this.updateFillExtriusionVisibility(false);
        }

        if (!this.state.change.value.streetNames.value) {
            this.updateSymbolsVisibility(false);
        }

        // No other way of changing the sprite sheet without using setStyle
        const mapStyle = this.map.getStyle();
        if (mapStyle.sprite !== sprite && sprite != null) {
            this.map.setStyle({ ...mapStyle, sprite });
        }

        this.map.once("idle", () => {
            if (this.mapBackgroundPolygonFilter) {
                this.mapBackgroundPolygonFilter.filterMapFeaturesWithinPolygon();
            }
            this.onFinishedLoadingBackground();
            this.loaded = true;
        });
    }

    private onError(event: MapboxError) {
        // Each vector tile source can have two parts (raster sources don't have TileJSON):
        // - Map tiles (.pbf, .webp, .jpeg) - Content of the tiles.
        // - TileJSON (.json) - Metadata for the tileset (https://docs.mapbox.com/help/glossary/tilejson/)

        // When Mapbox tile request fails it throws a AJAXError, this contains the sourceId, response status and resource url
        if (isMapboxTileSourceError(event) && this.sourceIds.includes(event.sourceId) && event.error.status != null) {
            // If raster or has is a vector tile (.pbf), we know it is a tile request
            if (event.source.type === "raster" || event.error.url.includes(".pbf")) {
                this.onTileErrorTracker.addEvent(event.error.status);
            } else {
                // Otherwise we know it is TileJSON file request
                this.onTileJSONErrorTracker.addEvent(event.error.status);
            }
        }
    }

    public updateFillExtriusionVisibility(show: boolean) {
        this.layers.forEach(({ id }) => {
            const layer = this.map.getLayer(id);
            // Hide or show all 3d areas in the map background
            if (layer != null && layer.type === "fill-extrusion") {
                this.map.setLayoutProperty(layer.id, "visibility", show ? "visible" : "none");
            }

            // Hide or show 2d areas in their place
            if (layer != null && layer.type === "fill") {
                this.map.setLayerZoomRange(layer.id, layer.minzoom ?? MAPBOX_MIN_ZOOM, show ? layer.maxzoom ?? MAPBOX_MAX_ZOOM : MAPBOX_MAX_ZOOM);
            }
        });
    }

    public updateSymbolsVisibility(show: boolean) {
        this.layers.forEach(({ id }) => {
            const layer = this.map.getLayer(id);
            // Hide or show all symbols in the map background
            if (layer != null && layer.type === "symbol") {
                this.map.setLayoutProperty(layer.id, "visibility", show ? "visible" : "none");
            }
        });
    }

    public remove() {
        super.remove();
        this.map.off("error", this.onErrorRef);
        this.mapBackgroundPolygonFilter?.remove();
    }
}
